import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../context/AuthContext';
import { cereriAPI, analyticsAPI } from '../services/api';
import { Card, StatCard, StatusBadge, Spinner, PageHeader, Button } from '../components/common';
import { formatRelativ, truncate } from '../utils';
import Layout from '../components/layout/Layout';
import { useWindowWidth } from '../hooks/useWindowWidth';

export default function DashboardPage() {
  const { user, isFunctionar } = useAuth();

  return (
    <Layout>
      {isFunctionar
        ? <DashboardFunctionar user={user} />
        : <DashboardCetatean  user={user} />
      }
    </Layout>
  );
}

// ── Dashboard Cetățean ────────────────────────────────────────
function DashboardCetatean({ user }) {
  const w = useWindowWidth();
  const isMobile = w < 640;

  const { data, isLoading } = useQuery(
    'cereri-mele',
    () => cereriAPI.list({ limit: 5 }).then(r => r.data),
    { staleTime: 30000 }
  );

  const cereri = data?.data || [];
  const stats  = {
    total:      data?.pagination?.total || 0,
    inLucru:    cereri.filter(c => c.status === 'in_lucru').length,
    rezolvate:  cereri.filter(c => c.status === 'rezolvata').length,
  };

  const grid3 = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',
    gap: isMobile ? '10px' : '16px',
    marginBottom: '20px',
  };

  const stepsGrid = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',
    gap: '12px',
  };

  const quickActions = [
    { to: '/cereri/noua', icon: '📝', label: 'Cerere nouă',    desc: 'Depune o cerere online' },
    { to: '/plati',       icon: '💳', label: 'Plăți online',   desc: 'Achită taxe și impozite' },
    { to: '/programari',  icon: '📅', label: 'Programare',     desc: 'Rezervă un interval' },
  ];

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' }}>
          Bună ziua, {user?.prenume}!
        </h1>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          Urmărește și gestionează cererile tale
        </p>
      </div>

      {/* Scurtături rapide */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
        {quickActions.map(a => (
          <Link key={a.to} to={a.to} style={quickCard}>
            <span style={{ fontSize: '28px' }}>{a.icon}</span>
            <div>
              <div style={{ fontWeight: '600', color: '#1a1a2e', fontSize: '14px' }}>{a.label}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{a.desc}</div>
            </div>
          </Link>
        ))}
      </div>

      <div style={grid3}>
        <StatCard label="Total cereri"  value={stats.total}     color="#2d2d7e" />
        <StatCard label="În lucru"      value={stats.inLucru}   color="#854f0b" />
        <StatCard label="Rezolvate"     value={stats.rezolvate} color="#3b6d11" />
      </div>

      <Card title="Cererile mele recente" action={<Link to="/cereri" style={linkStyle}>Vezi toate →</Link>}>
        {isLoading ? <Spinner /> : cereri.length === 0
          ? <EmptyDash text="Nu ai cereri depuse încă." />
          : cereri.map(c => <CerereRow key={c.id} cerere={c} isMobile={isMobile} />)
        }
      </Card>
    </div>
  );
}

// ── Dashboard Funcționar ──────────────────────────────────────
function DashboardFunctionar({ user }) {
  const w = useWindowWidth();
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;

  const { data: overview, isLoading } = useQuery(
    'analytics-overview',
    () => analyticsAPI.overview().then(r => r.data.data),
    { staleTime: 60000 }
  );

  const { data: cereriData } = useQuery(
    'cereri-recente',
    () => cereriAPI.list({ limit: 8 }).then(r => r.data)
  );

  if (isLoading) return <Spinner message="Se încarcă datele..." />;

  const grid4 = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : isTablet ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
    gap: isMobile ? '10px' : '16px',
    marginBottom: '20px',
  };

  const row2col = {
    display: 'grid',
    gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 320px',
    gap: '16px',
  };

  return (
    <div>
      <PageHeader
        title={`Bine ai venit, ${user?.prenume}!`}
        subtitle={`${new Date().toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
        action={
          !isMobile
            ? <Link to="/analytics"><Button variant="secondary">Vezi statistici complete →</Button></Link>
            : null
        }
      />

      <div style={grid4}>
        <StatCard label="Total cereri"      value={overview?.total || 0}       color="#2d2d7e" />
        <StatCard label="Timp mediu (zile)" value={overview?.timpMediu || 0}   color="#854f0b" />
        <StatCard label="SLA depășit"       value={overview?.slaDepasit || 0}  color="#a32d2d" />
        <StatCard label="Rezolvate azi"     value={getAzi(cereriData?.data)}   color="#3b6d11" />
      </div>

      {isMobile && (
        <div style={{ marginBottom: '16px' }}>
          <Link to="/analytics">
            <Button variant="secondary" style={{ width: '100%' }}>Vezi statistici complete →</Button>
          </Link>
        </div>
      )}

      <div style={row2col}>
        <Card title="Cereri recente" action={<Link to="/cereri" style={linkStyle}>Toate →</Link>}>
          {(cereriData?.data || []).map(c => (
            <CerereRow key={c.id} cerere={c} isMobile={isMobile} />
          ))}
        </Card>

        <Card title="Status cereri">
          {(overview?.peStatus || []).map(s => (
            <div key={s.status} style={statusRow}>
              <StatusBadge status={s.status} />
              <span style={statusCount}>{s.numar}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── Sub-componente ────────────────────────────────────────────
function CerereRow({ cerere, isMobile }) {
  return (
    <Link to={`/cereri/${cerere.id}`} style={isMobile ? rowLinkMobile : rowLink}>
      <div style={rowMain}>
        <span style={rowNumar}>{cerere.numar_cerere}</span>
        <span style={rowTitlu}>{truncate(cerere.titlu, isMobile ? 35 : 60)}</span>
      </div>
      <div style={rowRight}>
        <StatusBadge status={cerere.status} />
        {!isMobile && <span style={rowData}>{formatRelativ(cerere.created_at)}</span>}
      </div>
    </Link>
  );
}

function EmptyDash({ text }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px', color: '#888' }}>
      <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
      <p style={{ margin: 0 }}>{text}</p>
      <Link to="/cereri/noua" style={{ ...linkStyle, marginTop: '16px', display: 'inline-block' }}>
        Depune prima ta cerere →
      </Link>
    </div>
  );
}

function getAzi(cereri = []) {
  const azi = new Date().toDateString();
  return cereri.filter(c => c.status === 'rezolvata' && new Date(c.updated_at).toDateString() === azi).length;
}

// ── Styles ────────────────────────────────────────────────────
const stepItem       = { textAlign: 'center', padding: '16px' };
const stepItemMobile = { display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 0', borderBottom: '1px solid #f0f0f0' };
const stepNr         = { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#2d2d7e', color: '#fff', fontSize: '18px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', flexShrink: 0 };
const stepNrMobile   = { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#2d2d7e', color: '#fff', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const stepText       = { fontSize: '14px', color: '#555' };
const rowLink        = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0', textDecoration: 'none', gap: '12px' };
const rowLinkMobile  = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', textDecoration: 'none', gap: '8px' };
const rowMain        = { display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 };
const rowNumar       = { fontSize: '11px', color: '#888', flexShrink: 0, fontFamily: 'monospace' };
const rowTitlu       = { fontSize: '14px', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const rowRight       = { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 };
const rowData        = { fontSize: '12px', color: '#aaa' };
const statusRow      = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f5f5f5' };
const statusCount    = { fontWeight: '700', color: '#1a1a2e', fontSize: '16px' };
const linkStyle  = { color: '#2d2d7e', textDecoration: 'none', fontSize: '13px', fontWeight: '600' };
const quickCard  = { display: 'flex', alignItems: 'center', gap: '14px', backgroundColor: '#fff', borderRadius: '12px', padding: '16px', textDecoration: 'none', border: '1.5px solid #e5e7eb', transition: 'border-color .15s' };
